import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { posthog } from '../../utils/analytics';
import './FeedbackSurvey.css';

const SURVEY_ID = '019c483d-9482-0000-a027-910844303381';

const EMOJI_RATINGS = [
  { value: 1, emoji: '😡' },
  { value: 2, emoji: '😕' },
  { value: 3, emoji: '😐' },
  { value: 4, emoji: '🙂' },
  { value: 5, emoji: '😍' },
];

const STEP_RATING = 'rating';
const STEP_COMMENT = 'comment';
const STEP_THANKS = 'thanks';

function FeedbackSurvey({ show, onDismiss }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(STEP_RATING);
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const shownRef = useRef(false);

  useEffect(() => {
    if (show && !shownRef.current) {
      shownRef.current = true;
      posthog.capture('survey shown', { $survey_id: SURVEY_ID });
    }
  }, [show]);

  if (!show) return null;

  const handleRatingSelect = (value) => {
    setRating(value);
    setStep(STEP_COMMENT);
  };

  const handleDismiss = () => {
    posthog.capture('survey dismissed', { $survey_id: SURVEY_ID });
    onDismiss();
  };

  const handleSubmit = () => {
    const responses = {
      $survey_id: SURVEY_ID,
      $survey_response: rating,
    };
    if (comment.trim()) {
      responses.$survey_response_1 = comment.trim();
    }
    posthog.capture('survey sent', responses);
    setStep(STEP_THANKS);
  };

  return (
    <div className="feedback-overlay" onClick={handleDismiss}>
      <div className="feedback-dialog" onClick={(e) => e.stopPropagation()}>
        <button className="feedback-close" onClick={handleDismiss} aria-label="Close">
          ✕
        </button>

        {step === STEP_RATING && (
          <div className="feedback-step">
            <h2 className="feedback-title">{t('feedback.ratingTitle')}</h2>
            <p className="feedback-subtitle">{t('feedback.ratingSubtitle')}</p>
            <div className="feedback-emojis">
              {EMOJI_RATINGS.map(({ value, emoji }) => (
                <button
                  key={value}
                  className="feedback-emoji-btn"
                  onClick={() => handleRatingSelect(value)}
                  aria-label={`${value}/5`}
                >
                  <span className="feedback-emoji">{emoji}</span>
                </button>
              ))}
            </div>
            <button className="feedback-btn-later" onClick={handleDismiss}>
              {t('feedback.maybeLater')}
            </button>
          </div>
        )}

        {step === STEP_COMMENT && (
          <div className="feedback-step">
            <h2 className="feedback-title">{t('feedback.commentTitle')}</h2>
            <p className="feedback-subtitle">{t('feedback.commentSubtitle')}</p>
            <textarea
              className="feedback-textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('feedback.commentPlaceholder')}
              rows={4}
              autoFocus
            />
            <button className="feedback-btn-primary" onClick={handleSubmit}>
              {t('feedback.submit')}
            </button>
            <button className="feedback-btn-later" onClick={handleSubmit}>
              {t('feedback.skip')}
            </button>
          </div>
        )}

        {step === STEP_THANKS && (
          <div className="feedback-step">
            <h2 className="feedback-title">{t('feedback.thanksTitle')}</h2>
            <p className="feedback-subtitle">{t('feedback.thanksSubtitle')}</p>
            <a
              href="https://x.com/tradermap_io"
              target="_blank"
              rel="noopener noreferrer"
              className="feedback-btn-primary feedback-btn-x"
            >
              {t('feedback.followX')}
            </a>
            <button className="feedback-btn-later" onClick={onDismiss}>
              {t('common.close')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default FeedbackSurvey;
